
import {
  defineComponent,
  defineExpose,
  ref,
  reactive,
  computed,
  onMounted,
} from 'vue';

import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import OverlayPanel from 'primevue/overlaypanel';
import Button from 'primevue/button';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import Loader from '@/components/layout/Loader.vue';

import { showToastError, showToastSuccess } from '@/helpers';
import {
  saveShopParamsOnRedis,
  fetchShopConfigurationsNest,
  saveShopConfigurationsNest,
} from '@/composables/shop/ShopsConfiguration';
import { useUtmDropdownOptions, DropdownOption } from '@/composables/flows/useUtmDropdownOptions';
import { UserState } from '@/composables/User';

import { useI18n } from 'vue-i18n';

const UTM_DEFAULTS: Record<string, string> = {
  shopRequiredDataUtm_source: 'shopimind',
  shopRequiredDataUtm_medium: '{var=channel}',
  shopRequiredDataUtm_campaign: '{var=campaign_name}',
  shopRequiredDataUtm_id: '',
  shopRequiredDataUtm_content: '',
  shopRequiredDataUtm_term: '',
};

export default defineComponent({
  name: 'UtmServiceParameter',

  components: {
    InputText,
    InputSwitch,
    OverlayPanel,
    Button,
    SpmButton,
    Loader,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['close'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const { dropdownOptions, loadCustomData } = useUtmDropdownOptions(t);

    const isLoading = ref(false);
    const saveLoading = ref(false);
    const utmActive = ref(true);

    const field = reactive<Record<string, string>>({
      shopRequiredDataUtm_source: '',
      shopRequiredDataUtm_medium: '',
      shopRequiredDataUtm_campaign: '',
      shopRequiredDataUtm_id: '',
      shopRequiredDataUtm_content: '',
      shopRequiredDataUtm_term: '',
    });

    // Custom UTM params
    const customParams = ref<Array<{ key: string; value: string }>>([]);

    const addCustomParam = () => {
      customParams.value.push({ key: '', value: '' });
    };

    const removeCustomParam = (index: number) => {
      customParams.value.splice(index, 1);
    };

    // Variable picker state
    const variablePickerRef = ref();
    const variableFilter = ref('');
    const activeField = ref('');
    const activeCustomIndex = ref(-1);

    const filteredOptions = computed(() => {
      if (!variableFilter.value) return dropdownOptions.value;
      const q = variableFilter.value.toLowerCase();
      return dropdownOptions.value.filter(
        (opt) => opt.displayLabel.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
      );
    });

    const openVariablePicker = (event: Event, fieldKey: string) => {
      activeField.value = fieldKey;
      activeCustomIndex.value = -1;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const openVariablePickerCustom = (event: Event, index: number) => {
      activeField.value = '';
      activeCustomIndex.value = index;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const selectVariable = (opt: DropdownOption) => {
      if (activeCustomIndex.value >= 0) {
        customParams.value[activeCustomIndex.value].value = opt.value;
      } else {
        field[activeField.value] = opt.value;
      }
      variablePickerRef.value.hide();
    };

    const escapeHtml = (str: string) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const previewUrl = computed(() => {
      const shopUrl = UserState.activeShop?.url || 'https://example.com';
      const baseUrl = shopUrl.startsWith('http') ? shopUrl : `https://${shopUrl}`;
      const source = escapeHtml(field.shopRequiredDataUtm_source || 'shopimind');
      const medium = escapeHtml(field.shopRequiredDataUtm_medium || '{var=channel}');
      const campaign = escapeHtml(field.shopRequiredDataUtm_campaign || '{var=campaign_name}');
      let url = `${escapeHtml(baseUrl)}?utm_source=<strong>${source}</strong>&amp;utm_medium=<strong>${medium}</strong>&amp;utm_campaign=<strong>${campaign}</strong>`;
      if (field.shopRequiredDataUtm_id) {
        url += `&amp;utm_id=<strong>${escapeHtml(field.shopRequiredDataUtm_id)}</strong>`;
      }
      if (field.shopRequiredDataUtm_content) {
        url += `&amp;utm_content=<strong>${escapeHtml(field.shopRequiredDataUtm_content)}</strong>`;
      }
      if (field.shopRequiredDataUtm_term) {
        url += `&amp;utm_term=<strong>${escapeHtml(field.shopRequiredDataUtm_term)}</strong>`;
      }
      for (const param of customParams.value) {
        if (param.key && param.value) {
          url += `&amp;${escapeHtml(param.key)}=<strong>${escapeHtml(param.value)}</strong>`;
        }
      }
      return url;
    });

    const handleSave = async () => {
      const configurationsToSave: Array<{ key: string; value: string; lang: string | null }> = [
        { key: 'shopRequiredDataUtm_activeConfig', value: utmActive.value ? '1' : '0', lang: null },
      ];

      Object.entries(field).forEach(([key, value]) => {
        configurationsToSave.push({
          key,
          value: value ?? '',
          lang: null,
        });
      });

      // Save custom params as JSON
      const validCustomParams = customParams.value.filter((p) => p.key);
      configurationsToSave.push({
        key: 'shopRequiredDataUtm_customParams',
        value: JSON.stringify(validCustomParams),
        lang: null,
      });

      if (props.globalSave) {
        return {
          err: null,
          configs: configurationsToSave.map((config) => ({
            key: config.key,
            value: config.value,
            lang: config.lang || '',
          })),
        };
      }

      saveLoading.value = true;

      try {
        const result = await saveShopConfigurationsNest(props.shopId, configurationsToSave);
        if (result) {
          await saveShopParamsOnRedis(props.shopId);
          showToastSuccess('savedSuccessful');
        } else {
          showToastError('GENERIC_ERROR');
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        saveLoading.value = false;
      }
    };

    defineExpose({
      handleSave,
    });

    onMounted(async () => {
      try {
        isLoading.value = true;

        const [data] = await Promise.all([
          fetchShopConfigurationsNest(props.shopId, [
            'shopRequiredDataUtm_activeConfig',
            ...Object.keys(field),
            'shopRequiredDataUtm_customParams',
          ]),
          loadCustomData(),
        ]);

        if (data) {
          if ('shopRequiredDataUtm_activeConfig' in data) {
            utmActive.value = data.shopRequiredDataUtm_activeConfig !== '0';
          }

          Object.keys(field).forEach((key) => {
            if (key in data && data[key] !== null && data[key] !== undefined) {
              field[key] = data[key];
            }
          });

          // Load custom params
          if (data.shopRequiredDataUtm_customParams) {
            try {
              const parsed = JSON.parse(data.shopRequiredDataUtm_customParams);
              if (Array.isArray(parsed)) {
                customParams.value = parsed;
              }
            } catch {
              // Invalid JSON, ignore
            }
          }
        }

        // Apply defaults for fields that are still empty
        Object.keys(field).forEach((key) => {
          if (!field[key] && UTM_DEFAULTS[key]) {
            field[key] = UTM_DEFAULTS[key];
          }
        });
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      t,
      isLoading,
      saveLoading,
      utmActive,
      field,
      customParams,
      addCustomParam,
      removeCustomParam,
      previewUrl,
      handleSave,
      dropdownOptions,
      variablePickerRef,
      variableFilter,
      filteredOptions,
      openVariablePicker,
      openVariablePickerCustom,
      selectVariable,
    };
  },
});
